import { useState } from 'react';
import { useQuery } from 'react-apollo';

import {
  ACCOUNT_CARD_STATUS,
  CARD_TYPE,
} from '../../../../../../services/cards/constants';
import getUserAccountCardsQuery from '../../graphql/queries/getUserAccountCards';
import { createLoadMoreMethod } from '../../../../../../util/apollo';
import {
  CUSTOM_PRINT_SUBTYPE,
  CUSTOM_PRINT_TYPE,
} from '../../../../../../constants/pro-custom-prints';
import useAuthenticated from '../../../../../../hooks/useAuthenticated';
import { CREATION_TYPE } from '../../constants';

const useAccountCardCreations = ({ limit = 10 }) => {
  const [type, setType] = useState(CREATION_TYPE.WALDOGRAM);
  const authenticated = useAuthenticated();

  const {
    data: { me: meWithDrafts } = {},
    loading: loadingDrafts,
    error: draftsError,
    fetchMore: fetchMoreDrafts,
  } = useQuery(getUserAccountCardsQuery, {
    variables: {
      status: ACCOUNT_CARD_STATUS.DRAFT,
      limit,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !authenticated,
  });

  const {
    data: { me: meWithSent } = {},
    loading: loadingSent,
    error: sentError,
    fetchMore: fetchMoreSent,
  } = useQuery(getUserAccountCardsQuery, {
    variables: {
      status: ACCOUNT_CARD_STATUS.SHIPPED,
      limit,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !authenticated,
  });

  const {
    data: { me: meWithCollages } = {},
    loading: loadingCollages,
    error: collagesError,
    fetchMore: fetchMoreCollages,
  } = useQuery(getUserAccountCardsQuery, {
    variables: {
      cardType: CARD_TYPE.USER_CARD,
      customPrintTypes: [CUSTOM_PRINT_TYPE.PRINT_PRODUCT],
      customPrintSubtype: CUSTOM_PRINT_SUBTYPE.COLLAGE,
      limit,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !authenticated,
  });

  const loadMoreDrafts = createLoadMoreMethod({
    source: meWithDrafts,
    recordsPath: 'account_cards',
    limit,
    fetchMore: fetchMoreDrafts,
  });

  const loadMoreSent = createLoadMoreMethod({
    source: meWithSent,
    recordsPath: 'account_cards',
    limit,
    fetchMore: fetchMoreSent,
  });

  const loadMoreCollages = createLoadMoreMethod({
    source: meWithCollages,
    recordsPath: 'account_cards',
    limit,
    fetchMore: fetchMoreCollages,
  });

  return {
    type,
    setType,
    loadingCollages,
    loadingWaldograms: loadingDrafts || loadingSent,
    error: draftsError || sentError || collagesError,
    loadMoreDrafts,
    loadMoreSent,
    loadMoreCollages,
    collageAccountCards: meWithCollages?.account_cards ?? [],
    draftAccountCards: meWithDrafts?.account_cards ?? [],
    sentAccountCards: meWithSent?.account_cards ?? [],
  };
};

export default useAccountCardCreations;
